import { useCallback, useEffect, useRef, useState } from 'react';
import { NewBornFilterDTO, NewBornSummaryDTO } from '../../dto/newborn/NewBornDTO';
import { getNewBornSummary, isAccessDenied, isRequestCanceled } from '../../services/NewBornController';

/** `ready`: hay un resultado (puede estar actualizándose); `denied`: el perfil no tiene acceso a la pantalla. */
export type NewBornSummaryStatus = 'loading' | 'ready' | 'error' | 'denied';

/**
 * Resumen del período: se vuelve a pedir cada vez que cambian los filtros aplicados (período y «Más filtros»; no los
 * filtros de columna de la grilla). Cada consulta cancela la anterior, así que una respuesta vieja nunca pisa a una nueva.
 * Mientras consulta conserva el resultado anterior (`summary`) para que la pantalla lo muestre desenfocado.
 */
export default function useNewBornSummary(applied: NewBornFilterDTO) {
    const [summary, setSummary] = useState<NewBornSummaryDTO | null>(null);
    const [status, setStatus] = useState<NewBornSummaryStatus>('loading');
    const controllerRef = useRef<AbortController | null>(null);

    const run = useCallback(async (filters: NewBornFilterDTO) => {
        controllerRef.current?.abort();
        const controller = new AbortController();
        controllerRef.current = controller;
        setStatus('loading');
        try {
            const result = await getNewBornSummary(filters, controller.signal);
            if (controller.signal.aborted) return;
            setSummary(result);
            setStatus('ready');
        } catch (error: unknown) {
            if (controller.signal.aborted || isRequestCanceled(error)) return;
            setStatus(isAccessDenied(error) ? 'denied' : 'error');
        }
    }, []);

    useEffect(() => {
        void run(applied);
    }, [applied, run]);

    // Al desmontar se cancela lo que esté en vuelo
    useEffect(() => () => controllerRef.current?.abort(), []);

    const retry = useCallback(() => void run(applied), [applied, run]);

    return { summary, status, retry };
}
